import React from 'react';
import { motion } from 'framer-motion';
import { UseCaseSectionProps } from '@/types/components';
import { FileText, FolderOpen, GitBranch, Users } from 'lucide-react';

const USE_CASES = [
  {
    title: "The 30-Second Integration Revolution",
    description: "Watch complex data flows between your policy admin system, account management platform, and downstream applications get orchestrated in under 30 seconds. Our AI reads documents, cross-references data, and routes information faster than humanly possible.",
    requestedBy: "Fortune 500 Life Insurer",
    icon: "FileText"
  },
  {
    title: "Legacy System Time Machine",
    description: "Transform your 1980s mainframe into a 2025 AI powerhouse without touching a single line of legacy code. Watch decades-old systems suddenly understand natural language and make intelligent decisions through seamless API integration.",
    requestedBy: "Regional Property & Casualty Leader",
    icon: "FolderOpen"
  },
  {
    title: "The Data Orchestration Crystal Ball",
    description: "Our AI analyzes 1000+ data points across your entire system ecosystem in milliseconds, predicting workflow bottlenecks with 97% accuracy. What used to require weeks of analysis now happens before you finish reading this sentence.",
    requestedBy: "Specialty Commercial Insurer",
    icon: "GitBranch"
  },
  {
    title: "Unified System Experience Magic",
    description: "Turn fragmented system interactions into seamless workflows. Policy updates that required multiple system logins now happen through a single interface. Data that lived in silos now flows intelligently between applications.",
    requestedBy: "Digital-First Insurance Startup",
    icon: "Users"
  }
];

const IconMap = {
  FileText,
  FolderOpen,
  GitBranch,
  Users
};

const UseCaseSection: React.FC<UseCaseSectionProps> = ({ className }) => {
  return (
    <section id="use-cases" className="py-20 bg-gradient-to-b from-emerald-900 to-gray-900">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-emerald-400"
          >
            Example Insurance Use Cases
          </motion.h2>
          <p className="text-2xl text-gray-400">
            Real Solutions for Real Problems
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {USE_CASES.map((useCase, index) => {
            const Icon = IconMap[useCase.icon as keyof typeof IconMap];
            
            return (
              <motion.div
                key={useCase.title}
                className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="flex items-start gap-6">
                  <Icon className="w-12 h-12 text-emerald-400 flex-shrink-0" />
                  <div>
                    <h3 className="text-2xl md:text-3xl font-semibold mb-3 text-white">
                      {useCase.title}
                    </h3>
                    <p className="text-xl text-gray-400 mb-4">
                      {useCase.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default UseCaseSection;
